import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  useTheme,
  alpha,
  Paper,
  Slide,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import DownloadIcon from '@mui/icons-material/Download';
import InstallMobileIcon from '@mui/icons-material/InstallMobile';
import { DEFAULT_THEME } from '../../constants/themeDefaults';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const PWAInstallPrompt: React.FC = () => {
  const theme = DEFAULT_THEME;
  const [isOpen, setIsOpen] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showManualInstructions, setShowManualInstructions] = useState(false);
  const [installPlatform, setInstallPlatform] = useState<'ios' | 'android' | 'desktop' | ''>('');

  useEffect(() => {
    // Detect the user's platform
    const userAgent = navigator.userAgent || navigator.vendor;
    if (/iPad|iPhone|iPod/.test(userAgent)) {
      setInstallPlatform('ios');
    } else if (/android/i.test(userAgent)) {
      setInstallPlatform('android');
    } else {
      setInstallPlatform('desktop');
    }

    // Listen for the beforeinstallprompt event
    const handleBeforeInstallPrompt = (e: Event) => {
      // Prevent Chrome 67 and earlier from automatically showing the prompt
      e.preventDefault();
      // Store the event so it can be triggered later
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      // Show the install button
      setIsOpen(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Check if the user is already using the PWA
    if (window.matchMedia('(display-mode: standalone)').matches) {
      // User is already using the PWA, don't show install prompt
      setIsOpen(false);
    } else {
      // Show the prompt after 30 seconds if user is on mobile
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      if (isMobile) {
        const timer = setTimeout(() => {
          setIsOpen(true);
        }, 30000);
        return () => clearTimeout(timer);
      }
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleInstall = async () => {
    if (deferredPrompt) {
      // Show the install prompt
      deferredPrompt.prompt();
      // Wait for the user to respond to the prompt
      const { outcome } = await deferredPrompt.userChoice;
      // Clear the deferredPrompt variable, as it can only be used once
      setDeferredPrompt(null);
      // Hide the dialog regardless of outcome
      setIsOpen(false);
    } else {
      // If no deferredPrompt, show manual instructions
      setShowManualInstructions(true);
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <Slide direction="up" in={isOpen} mountOnEnter unmountOnExit>
      <Paper 
        elevation={4} 
        sx={{ 
          position: 'fixed', 
          bottom: 16, 
          left: 16, 
          right: 16, 
          maxWidth: 600, 
          mx: 'auto', 
          zIndex: 1300, 
          p: 2, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between', 
          gap: 2,
          bgcolor: theme.navigationColor,
          color: theme.secondaryColor
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <InstallMobileIcon />
          <Typography variant="body2">
            Install MindShare for quick access from your home screen.
          </Typography>
        </Box>
        <Box>
          <Button 
            color="inherit" 
            size="small" 
            onClick={handleInstall} 
            sx={{ mr: 1, color: theme.secondaryColor }}
          >
            Install
          </Button>
          <IconButton size="small" onClick={handleClose} color="inherit">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Paper>
    </Slide>
  );
};

export default PWAInstallPrompt; 